/* eslint react-hooks/exhaustive-deps: "warn" */

import { type ReactNode } from 'react';

import cn from 'classnames';

import { AnimatedNumber } from 'ui/atoms/AnimatedNumber/AnimatedNumber';
import { type FeatureGridItem } from 'ui/atoms/FeatureGrid/FeatureGrid';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './StatsBlock.module.scss';

const baseClass = 'stats-block';

type Props = {
  extra?: ReactNode;
  title?: string;
  description?: string;
  theme?: 'light' | 'dark';
  className?: string;
  hasPadding?: boolean;
  hasBackground?: boolean;
  /** Removes the wrapper's own section background so only the stat cards show, letting whatever sits behind it (e.g. a hero) show through. */
  transparent?: boolean;
  features?: FeatureGridItem[];
};

const defaultFeatures: FeatureGridItem[] = [
  {
    value: '92',
    suffix: '%',
    title: 'Cost Reduction',
  },
  {
    value: '300',
    suffix: '+M',
    title: 'Company Profiles',
  },
  {
    value: '500',
    suffix: '+',
    title: 'Data Sources',
  },
  {
    value: '200',
    suffix: '',
    title: 'Countries Covered',
  },
];

export const StatsBlock = ({
  extra,
  title,
  description,
  theme,
  className,
  hasPadding = true,
  hasBackground = true,
  transparent = false,
  features,
}: Props) => {
  const featuresToUse = features || defaultFeatures;

  return (
    <section
      className={cn(
        styles[`${baseClass}-wrapper`],
        hasPadding && styles[`${baseClass}--has-padding`],
        theme === 'dark' && styles[`${baseClass}--dark`],
        transparent && styles[`${baseClass}--transparent`],
        className,
      )}
    >
      <div className={styles[`${baseClass}-container`]}>
        {(extra || title || description) && (
          <TitleBlock extra={extra} title={title} description={description} theme={theme} />
        )}

        {featuresToUse && (
          <div className={cn(styles[`${baseClass}-grid`], hasBackground && styles[`${baseClass}-grid--cards`])}>
            {featuresToUse.map((item, idx) => {
              const numericValue = item.value ? parseInt(item.value, 10) : NaN;
              const isAnimatable = !Number.isNaN(numericValue);

              return (
                <div key={idx} className={styles[`${baseClass}-item`]}>
                  {isAnimatable ? (
                    <AnimatedNumber
                      value={numericValue}
                      prefix={item.prefix}
                      suffix={item.suffix}
                      className={styles[`${baseClass}-value`]}
                    />
                  ) : (
                    <p className={styles[`${baseClass}-value`]}>
                      {item.prefix}
                      {item.value}
                      {item.suffix}
                    </p>
                  )}

                  {item.title && <span className={styles[`${baseClass}-label`]}>{item.title}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
